import { ActionUpdateField, ActionsPropertiesDetails, PROPS_TYPE } from 'app/automations/automation-helpers-actions';
import { AutomationEditorConfig, CustomModelAction, TypeInput, TypeStep } from 'app/automations/automations-models';
import { IntegrationType } from 'app/automations/integrations-models';
import { IntegrationCapability } from './integration-capability';
import { UPDATE_SAFE_ADD_CONTACT_ACTIONS } from './update-safe-add-contact-actions';
import { UpdateListTarget } from './update-list-target';

// An entry forces a flag regardless of config shape, overriding auto-detection.
//
// Pipedrive auto-detects as fork-capable — it has a create-deal action plus a `PipedriveUpdateDeal`
// field-update action — but its "contact" is a deal/note/organization, so the Add/Update *contact*
// fork is semantically wrong for it. The fork is forced off here; add/update detection stays intact.
export const CAPABILITY_OVERRIDES: Readonly<Record<string, Partial<IntegrationCapability>>> = {
  [IntegrationType.Pipedrive]: { supportsAddUpdateFork: false },
};

const UPDATE_GROUPS: ReadonlyArray<string> = [TypeStep.FieldMapping, TypeStep.UpdateOtherFields];

const LIST_PROP_NAMES: ReadonlyArray<string> = [
  PROPS_TYPE.ListId,
  PROPS_TYPE.ListIds,
  PROPS_TYPE.Lists,
  PROPS_TYPE.TagIds,
];

export function isListPropName(name: string | undefined): boolean {
  return !!name && LIST_PROP_NAMES.includes(name);
}

/**
 * Null when the integration updates the contact wherever it already is (Voyado, Klaviyo, …).
 *
 * Reads the *live* config, so an integration whose list requirement was stripped for real time (see
 * `AutomationSerializer.excludeRealTimeProperties`) reports none. The property's shape comes from the
 * canonical action definition rather than the live `properties` array, because the serializer replaces the
 * field-mapping action's properties with fieldValue/fieldName pairs only.
 */
export function findUpdateListTarget(app: AutomationEditorConfig): UpdateListTarget | null {
  const requiredListActions: CustomModelAction[] = (app?.actionsConfig ?? [])
    .filter((group) => UPDATE_GROUPS.includes(group.nameGroup))
    .flatMap((group) => group.actionsGroup ?? [])
    .map((item) => item.action)
    .filter((action) => action?.requiredList === true);
  const actions = requiredListActions.filter((action) =>
    (ActionUpdateField as readonly string[]).includes(action.$type),
  );
  if (!actions.length) {
    return null;
  }
  const listProp = (ActionsPropertiesDetails[actions[0].$type]?.properties ?? []).find((prop) =>
    LIST_PROP_NAMES.includes(prop.name),
  );
  if (!listProp?.url) {
    return null;
  }
  return {
    actions,
    dependentActions: requiredListActions.filter((action) => !actions.includes(action)),
    propName: listProp.name,
    url: listProp.url,
    multiple: listProp.typeInput === TypeInput.ListMultiSelect,
  };
}

function groupHasCreateAction(config: AutomationEditorConfig): boolean {
  const addGroup = config.actionsConfig?.find((group) => group.nameGroup === TypeStep.AddContact);
  if (!addGroup) {
    return false;
  }
  return (
    addGroup.actionsGroup?.some(
      (item) =>
        item.action &&
        !(ActionUpdateField as readonly string[]).includes(item.action.$type) &&
        !UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(item.action.$type),
    ) ?? false
  );
}

function groupHasUpdateAction(config: AutomationEditorConfig): boolean {
  const updateGroups = config.actionsConfig?.filter((group) => UPDATE_GROUPS.includes(group.nameGroup)) ?? [];
  const hasFieldUpdate = updateGroups.some((group) =>
    group.actionsGroup?.some(
      (item) => item.action && (ActionUpdateField as readonly string[]).includes(item.action.$type),
    ),
  );
  // An integration whose only update-side action is an update-safe Add Contact action still needs the fork.
  const addGroup = config.actionsConfig?.find((group) => group.nameGroup === TypeStep.AddContact);
  const hasUpdateSafeAddContact =
    addGroup?.actionsGroup?.some(
      (item) => item.action && UPDATE_SAFE_ADD_CONTACT_ACTIONS.includes(item.action.$type),
    ) ?? false;
  return hasFieldUpdate || hasUpdateSafeAddContact;
}

export function deriveIntegrationCapabilities(configs: AutomationEditorConfig[]): Map<string, IntegrationCapability> {
  const capabilities = new Map<string, IntegrationCapability>();
  for (const config of configs ?? []) {
    const supportsAdd = groupHasCreateAction(config);
    const supportsUpdate = groupHasUpdateAction(config);
    const detected: IntegrationCapability = {
      name: config.name,
      supportsAdd,
      supportsUpdate,
      supportsAddUpdateFork: supportsAdd && supportsUpdate,
      updateRequiresList: !!findUpdateListTarget(config),
    };
    capabilities.set(config.name, { ...detected, ...CAPABILITY_OVERRIDES[config.name] });
  }
  return capabilities;
}
